import type { GroupItemVO, RangeVO } from "@/api"
import { deepClone } from "@/util/util"
import { useCreation, useSetState } from "ahooks-v2"
import { Button, message } from "antd"
import type { ForwardRefRenderFunction } from "react"
import { forwardRef, useCallback, useEffect, useImperativeHandle, useRef } from "react"
import "./index.scss"
// ==============================================
import UnitCombination from "./components/UnitCombination"
// ==============================================
// 将参数数据转换为state
function getStateByParams(values: RangeVO[]) {
  const params = {}
  const cloneValues = deepClone(values)
  for (const item of cloneValues) {
    params[item.key] = {
      options: item.options || [],
      showName: item.showName || "",
    }
    if (item.range) {
      params[item.key].range = item.range || []
    }
  }
  return params
}
// ==============================================
interface RangeComponentVO {
  componentKey: string
  visible: boolean
  data: RangeVO[]
  group: GroupItemVO[]
  onClose?: () => void // 关闭
  passData?: (type: string, data: any) => void // 传递数据到父组件
}
const RangeComponent: ForwardRefRenderFunction<any, RangeComponentVO> = (
  { componentKey, visible, data, group = [], passData, onClose },
  ref: any,
) => {
  const mapRef = useRef<any>({})
  const initState = useCreation(() => {
    // 将参数数据转换为state
    return getStateByParams(data)
  }, [data])
  const [state, set] = useSetState<any>(initState)

  // 赋值后的group
  const processedGroup: GroupItemVO[] = useCreation(() => {
    const cloneGroup = deepClone(group)
    for (const groupItem of cloneGroup) {
      const stateItem = state?.[groupItem.key]
      groupItem.choice.defaultValues = stateItem?.options?.slice() || []
      if (groupItem.customize) {
        groupItem.customize.defaultValues = stateItem?.range?.slice() || []
      }
    }
    return cloneGroup
  }, [group, state])

  // 确定
  const submitClick = useCallback(() => {
    const map = new Map()
    const emptyList = []
    for (const key in mapRef.current) {
      const element = mapRef.current?.[key]
      const { options, names, range, title, unit } = element?.getValues()
      // 自定义校验
      if (!element?.rangeVerify()) return
      // 选择或填写
      if (!options?.length && !range?.length) {
        if (options && range) {
          return message.error(`${title}请选择或填写自定义`)
        } else {
          emptyList.push(true)
        }
      }
      // showName
      let showName
      if (range?.length) {
        showName = `${range.join("-")}${unit}`
      } else if (!options?.length) {
        showName = `未选择${title}`
      } else if (options?.length === 1) {
        showName = names?.[0] || ""
      } else if (options?.length >= 2) {
        showName = "多选"
      }
      const result: any = {
        options,
        showName,
      }
      if (range) {
        result.range = range
      }
      map.set(key, result)
    }
    // 是否全部未选择
    if (emptyList?.length && emptyList?.length === Array.from(map.keys())?.length) {
      return message.error("至少选择一项")
    }
    // 传递数据到父组件
    passData && passData(componentKey, map)
    // 关闭
    onClose && onClose()
  }, [componentKey, passData, onClose])

  // 取消
  const closeClick = useCallback(() => {
    onClose && onClose()
  }, [onClose])

  // 重置为上一次的数据
  const resetBeforeData = useCallback(() => {
    // 将参数数据转换为state
    const params = getStateByParams(data)
    set(params)
  }, [data, set])

  // 重置为默认数据
  const resetClick = useCallback(() => {
    const params = {}
    const cloneGroup = deepClone(group)
    for (const item of cloneGroup) {
      params[item.key] = {
        options: item.choice.defaultValues || [],
        showName: item.defaultShowName,
      }
      if (item.customize) {
        params[item.key].range = item.customize.defaultValues || []
      }
    }
    set(params)
  }, [group, set])

  // 打开弹窗时，重置为上一次数据
  useEffect(() => {
    if (visible) {
      resetBeforeData() // 重置为上一次的数据
    }
  }, [visible, resetBeforeData])

  // 传递方法到父组件
  useImperativeHandle(ref, () => ({
    resetDefaultData: resetClick,
    resetBeforeData,
  }))

  return (
    <div className="range-components">
      {/* group */}
      {processedGroup?.map((item) => (
        <UnitCombination
          ref={(el) => (mapRef.current[item.key] = el)}
          key={item.key}
          choice={item.choice}
          customize={item.customize}
        />
      ))}
      <div className="btns">
        <Button className="reset theme-text-btn" type="text" onClick={resetClick}>
          重置
        </Button>
        <div>
          <Button className="cancel theme-cancel-btn" onClick={closeClick}>
            取消
          </Button>
          &nbsp;
          <Button className="submit theme-primary-btn" type="primary" onClick={submitClick}>
            确定
          </Button>
        </div>
      </div>
    </div>
  )
}

export default forwardRef(RangeComponent)
